import { useStyletron } from 'parkdepot-shared/theme'
import { TFunction } from 'i18next'
import { DateTime } from 'luxon'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'

// utils
import { formatPriceWithCurrency } from '../../../../utils/formatPriceWithCurrency.utils'
import { priceToString } from '../../../../utils/priceToString.utils'

// style components
import FlexContainer from '../../../../components/style/FlexContainer.style'
import Text from '../../../../components/style/Text.style'

// types
import { Checkout } from '../../../../types/checkout.interface'
import { LotViewName } from '../../../../types/lot.interface'
import { getNightParkingTimesFromConfigMemoized } from '../../../../utils/getNightParkingTimesFromConfig.utils'

interface Props {
    checkout: Checkout | null
}

const formatDuration = (t: TFunction, duration = 0) => {
    return `${duration} ${t(duration > 1 ? 'common.hours' : 'common.hour')}`
}

const DurationSection: FC<Props> = ({ checkout }) => {
    const { start_datetime, end_datetime, duration, price } = checkout || {}
    const [css, theme] = useStyletron()
    const { t } = useTranslation()
    const [sundayIncluded, setSundayIncluded] = React.useState(false)

    const NightParkingTimes = () => {
        const nightFeature = checkout?.lot?.active_features.find(
            (feature) => feature.name.toUpperCase() === LotViewName.NIGHTLY
        )
        if (!nightFeature) return null
        const { start, end } = getNightParkingTimesFromConfigMemoized(nightFeature)
        if ((nightFeature.config.night_parking_times?.length ?? 0) > 1) {
            setSundayIncluded(true)
        }

        return (
            <>
                {start} - {end} {t('checkout.hour')}
            </>
        )
    }

    const textWidthCss = { width: '12.5rem' }
    return (
        <div>
            <FlexContainer margintop="10px">
                <Text className={css(textWidthCss)} typography={theme.typography.ParagraphMedium} fontWeight={400}>
                    {checkout?.lotCheckoutType === LotViewName.SUBSCRIPTION
                        ? t('checkout.subscriptions.validity')
                        : t('checkout.duration')}
                </Text>
                <Text typography={theme.typography.ParagraphMedium} data-testid="parking-duration-date-text">
                    {checkout?.lotCheckoutType === LotViewName.HOURLY && DateTime.now().toFormat('dd.MM.yyyy')}
                    {(checkout?.lotCheckoutType === LotViewName.DAILY ||
                        checkout?.lotCheckoutType === LotViewName.NIGHTLY) &&
                        `${start_datetime instanceof DateTime ? start_datetime.toFormat('dd.MM.yyyy') : ''} - ${
                            end_datetime instanceof DateTime ? end_datetime.toFormat('dd.MM.yyyy') : ''
                        }`}
                    {checkout?.lotCheckoutType === LotViewName.SUBSCRIPTION &&
                        start_datetime instanceof DateTime &&
                        `${start_datetime.toFormat('dd.MM.yyyy')}`}
                </Text>
            </FlexContainer>
            {checkout?.lotCheckoutType === LotViewName.NIGHTLY && sundayIncluded && (
                <FlexContainer margintop="4px">
                    <Text className={css(textWidthCss)} typography={null}></Text>
                    <Text typography={theme.typography.ParagraphSmall}>{t('lot.sundayIncluded')}</Text>
                </FlexContainer>
            )}
            <FlexContainer margintop="14px">
                <Text className={css(textWidthCss)} typography={theme.typography.ParagraphMedium} fontWeight={400}>
                    {t('common.parkingTime')}
                </Text>
                <Text typography={theme.typography.ParagraphMedium} data-testid="parking-duration-text">
                    {checkout?.lotCheckoutType === LotViewName.HOURLY && formatDuration(t, duration)}
                    {checkout?.lotCheckoutType === LotViewName.DAILY && t('checkout.allDay')}
                    {checkout?.lotCheckoutType === LotViewName.NIGHTLY && (
                        <>
                            <NightParkingTimes />
                        </>
                    )}
                    {checkout?.lotCheckoutType === LotViewName.SUBSCRIPTION && t('subscriptions.duration')}
                </Text>
            </FlexContainer>
            <FlexContainer margintop="14px">
                <Text className={css(textWidthCss)} typography={theme.typography.ParagraphMedium} fontWeight={400}>
                    {t('common.parkingFee')}
                </Text>
                <Text typography={theme.typography.ParagraphMedium} data-testid="parking-price-text">
                    {formatPriceWithCurrency().format(priceToString(price || 0))}
                    {checkout?.lotCheckoutType === LotViewName.SUBSCRIPTION &&
                        ` / ${t('checkout.subscriptions.month')}`}
                </Text>
            </FlexContainer>
        </div>
    )
}

export default React.memo(DurationSection)
